import { HugeiconsIcon } from "@hugeicons/react";
import { VoiceIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Link } from "react-router-dom";
import { Avatar } from "shared/components/Avatar";
import { TRANSCRIPT_UNAVAILABLE } from "shared/app/Voicemail/voicemail";
import { cx } from "shared/utils";
import { formatCallDuration } from "shared/utils/missed-call";
import {
  buildMissedCallUrl,
  callerLabel,
  useMarkNotificationAsRead
} from "../helpers/call-notification";
import { formatCalendar } from "../helpers/format-calendar";
import { Notification } from "../types";
import { CallBackButton } from "./MissedCallNotificationCard";

// "New voicemail from Maria Lopez (0:42)".
const voicemailTitle = (metadata: Notification["metadata"]): string => {
  const duration = formatCallDuration(metadata.voicemail_duration_seconds);
  const title = `New voicemail from ${callerLabel(metadata)}`;
  return duration ? `${title} (${duration})` : title;
};

/**
 * CRM-1777: a caller nobody answered left a voicemail. It replaces the
 * missed-call notification for that call, and reads like it: the row opens
 * the Inbox conversation, Call back sits outside the link.
 */
export const VoicemailNotificationCard = ({ item }: { item: Notification }) => {
  const { id, read_at, url, metadata, created_at } = item;
  const markAsRead = useMarkNotificationAsRead();

  const handleClick = () => {
    if (!read_at) markAsRead([id]);
  };

  const callerName = callerLabel(metadata);
  const timeLabel = formatCalendar(metadata.occurred_at || created_at);
  const transcript = metadata.transcript_preview?.trim();

  return (
    <div className="flex items-stretch gap-6 bg-white p-6 hover:bg-alpha-40">
      <Link
        to={url || buildMissedCallUrl(metadata)}
        onClick={handleClick}
        className="flex min-w-0 flex-1 cursor-pointer flex-col gap-4"
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <HugeiconsIcon
            icon={VoiceIcon}
            size={16}
            className="shrink-0 text-primary-500"
            aria-hidden
          />
          <p className="truncate text-sm font-medium text-primary-500">
            {voicemailTitle(metadata)}
          </p>
        </div>

        <div className="flex min-w-0 items-center gap-4">
          <span
            className={cx(
              "size-3 shrink-0 rounded-full",
              read_at ? "bg-alpha-40" : "bg-primary-500"
            )}
          />
          <Avatar name={callerName} size={44} className="shrink-0 font-bold" />
          <div className="min-w-0">
            <p className="truncate text-base font-bold text-content-1000">
              {callerName}
            </p>
            <p
              className={cx(
                "truncate text-sm",
                transcript ? "text-content-400" : "text-content-200"
              )}
            >
              {transcript || TRANSCRIPT_UNAVAILABLE}
            </p>
          </div>
        </div>
      </Link>

      <div className="flex shrink-0 flex-col items-end justify-between gap-4">
        <p className="text-sm font-medium text-content-300">{timeLabel}</p>
        <CallBackButton metadata={metadata} />
      </div>
    </div>
  );
};
